<script setup>
import { onBeforeMount, onBeforeUnmount, watch, computed, ref } from "vue";
import { useLocale } from "vuetify";
import { useRouter, useRoute } from "vue-router";
import { useSmartech } from "@/composables/useSmartech.composable";
import { usePushNotifications } from "@/composables/usePushNotifications.composable";
import { isDevelopment, storageSwitcher } from "@/utils/storage.util";
import { usePushNotificationStore } from "@/stores/pushNotification.store";
import { storeToRefs } from "pinia";
import EnvSwitcher from "@/components/env-switcher/EnvSwitcher.vue";
import ForceUpdateDialog from "@/components/dialogs/force-update-dialog/ForceUpdateDialog.vue";

const { current, t } = useLocale();
const { init: initSmartech } = useSmartech();
const pushStore = usePushNotificationStore();
const router = useRouter();
const route = useRoute();
const { openAppSettings } = usePushNotifications();
const { foregroundNotification, pendingDeepLink, permissionDenied } = storeToRefs(pushStore);

const HOME_ROUTES = new Set(["Dashboard", "login", "auto-login"]);
const EXIT_WINDOW_MS = 2000;

const exitHintVisible = ref(false);
let lastBackAt = 0;
let exitHintTimer = null;
let backListenerHandle = null;

const snackbarVisible = computed({
  get: () => Boolean(foregroundNotification.value),
  set: (v) => {
    if (!v) pushStore.clearForegroundNotification();
  },
});

const snackbarTitle = computed(() => foregroundNotification.value?.title || "");
const snackbarBody = computed(() => foregroundNotification.value?.body || "");

const permissionBannerVisible = computed({
  get: () => Boolean(permissionDenied.value),
  set: (v) => {
    if (!v) pushStore.setPermissionDenied(false);
  },
});

function handleSnackbarClick() {
  const data = foregroundNotification.value?.data || {};
  const link = data.deepLink
    ? data.deepLink
    : data.type === "booking"
      ? `/booking?bookingId=${data.id ?? ""}`
      : data.type === "review"
        ? "/review-management"
        : data.type === "allotment" || data.type === "block_request"
          ? "/allotment"
          : data.type === "package"
            ? "/package"
            : data.type === "newsMarketing"
              ? "/news"
              : "/notifications";
  pushStore.clearForegroundNotification();
  router.push(link);
}

watch(pendingDeepLink, (link) => {
  if (link) {
    router.push(link);
    pushStore.clearPendingDeepLink();
  }
});

async function setupBackButton() {
  try {
    const { Capacitor } = await import("@capacitor/core");
    if (Capacitor.getPlatform() !== "android") return;
    const { App: CapApp } = await import("@capacitor/app");
    backListenerHandle = await CapApp.addListener("backButton", () => {
      if (HOME_ROUTES.has(route.name)) {
        const now = Date.now();
        if (now - lastBackAt < EXIT_WINDOW_MS) {
          exitHintVisible.value = false;
          if (exitHintTimer) clearTimeout(exitHintTimer);
          CapApp.exitApp();
        } else {
          lastBackAt = now;
          exitHintVisible.value = true;
          if (exitHintTimer) clearTimeout(exitHintTimer);
          exitHintTimer = setTimeout(() => {
            exitHintVisible.value = false;
          }, EXIT_WINDOW_MS);
        }
      } else if (window.history.length > 1) {
        router.back();
      } else {
        router.push({ name: "Dashboard" });
      }
    });
  } catch (e) {
    console.warn("[back-button] setup failed", e);
  }
}

onBeforeMount(async () => {
  await initSmartech();
  const storageLocale = window.localStorage.getItem("locale") || current.value;
  current.value = storageLocale;

  if (!isDevelopment) {
    storageSwitcher();
  }

  // Re-sync FCM token when app comes to foreground.
  // Covers: previously denied → now granted (toggled in system settings),
  // session restore after background, FCM token rotation while logged out.
  try {
    const { Capacitor } = await import("@capacitor/core");
    if (!Capacitor.isNativePlatform()) return;
    const { App: CapApp } = await import("@capacitor/app");
    const handle = await CapApp.addListener("appStateChange", (state) => {
      if (state.isActive) {
        console.log("[FCM] app resumed, syncing device token");
        void usePushNotifications().syncOnAppResume();
      }
    });
    onBeforeUnmount(() => {
      if (handle?.remove) handle.remove();
    });
  } catch (e) {
    console.warn("[FCM] appStateChange setup failed", e);
  }
});

onBeforeUnmount(() => {
  if (backListenerHandle && backListenerHandle.remove) backListenerHandle.remove();
  if (exitHintTimer) clearTimeout(exitHintTimer);
});

void setupBackButton();
</script>

<template>
  <v-app id="app">
    <router-view />

    <ToastMessage />
    <ForceUpdateDialog />
    <EnvSwitcher v-if="isDevelopment" />

    <v-snackbar
      v-model="snackbarVisible"
      :timeout="4000"
      location="top"
      color="primary"
      @click="handleSnackbarClick"
    >
      <div class="push-snackbar">
        <strong v-if="snackbarTitle">{{ snackbarTitle }}</strong>
        <div v-if="snackbarBody">{{ snackbarBody }}</div>
      </div>
    </v-snackbar>

    <v-snackbar v-model="permissionBannerVisible" :timeout="-1" location="top" color="warning">
      {{ t("$vuetify.layout.notificationSettings.disabledDescription") }}
      <template #actions>
        <v-btn variant="text" @click="openAppSettings">
          {{ t("$vuetify.layout.notificationSettings.openSettings") }}
        </v-btn>
        <v-btn variant="text" @click="pushStore.setPermissionDenied(false)">
          {{ t("$vuetify.layout.notificationSettings.dismiss") }}
        </v-btn>
      </template>
    </v-snackbar>

    <v-snackbar v-model="exitHintVisible" :timeout="2000" location="bottom" color="grey-darken-3">
      {{ t("$vuetify.layout.pressBackToExit") }}
    </v-snackbar>
  </v-app>
</template>

<style lang="scss">
@use "@/assets/scss/app.scss";
</style>

<style scoped>
.push-snackbar {
  cursor: pointer;
  strong {
    display: block;
    margin-bottom: 2px;
  }
}
</style>
